import { createReducer, createSelector } from 'redux-starter-kit';
import {
  putVehicle,
  putVehicles,
  putVehicleDamages,
  putVehiclesPostDamages,
  putVehicleRetirement,
  putVehicleUsage,
  putVehicleSavedUsage,
  putVehicleReinstated,
  deleteVehicle,
} from './vehicleActions';
import {getDateComparisonNumber} from "../../../sections/secured/bynx/packageUtil";


/**
 * Returns a keyed object representing all the vehicles in the store.
 * e.g. { 23: { ...vehicle23 }, 24: { ...vehicle24 }, ...etc }
 * @param {object} state The redux store for the application.
 * @example const vehicle = selectVehiclesMap(state)[vehicleId]
 */
export const selectVehiclesMap = state => state.via.domain.vehicles;


/**
 * Returns a list of the all the vehicles in the store.
 * @param {object} state The redux store for the application.
 * @example const vehicles = selectVehicles(state);
 */
export const selectVehicles = createSelector(['via.domain.vehicles'], map => (map ? Object.values(map) : null));

/**
 * Returns a single vehicle in the store.
 * @param {object} state The redux store for the application.
 * @param {number} vehicleId The id of the vehicle.
 * @example const vehicle = selectVehicle(state, vehicleId);
 */
export const selectVehicle = (state, vehicleId) => {
  const vehicleMap = selectVehiclesMap(state);
  if (!vehicleMap) return null;

  return vehicleMap[vehicleId];
};


const initialVehicles = {
  // vehicleId: { /* vehicle object */ }
  // vehicleId: { /* vehicle object */ }
  // ...etc
};

const vehicleReducer = createReducer(initialVehicles, {
  // Puts a new vehicle into the store.
  [putVehicle]: (state, action) => {
    const vehicle = action.payload;
    return {
      ...state,
      [vehicle.vehicleId]: vehicle,
    };
  },

  // Replaces all the vehicles in the store.
  [putVehicles]: (state, action) => {
    const vehicles = action.payload;

    // Builds a keyed object. vehicleId => vehicle
    return vehicles.reduce((newState, vehicle) => {
      newState[vehicle.vehicleId] = vehicle; // eslint-disable-line no-param-reassign
      return newState;
    }, { ...initialVehicles });
  },

  // Deletes a vehicle from the store
  [deleteVehicle]: (state, action) => {
    const vehicleId = action.payload;
    const newState = { ...state };
    delete newState[vehicleId];
    return newState;
  },

  // Replaces all the vehicles in the store.
  [putVehicleDamages]: (state, action) => {
    const vehicleDamages = action.payload;

    return {
      ...state,
      [vehicleDamages.vehicleId]: vehicleDamages,
    };
  },

  // Replaces all the vehicles in the store.
  [putVehiclesPostDamages]: (state, action) => {
    const vehicleDamages = action.payload;

    return {
      ...state,
      [vehicleDamages.vehicleId]: {
        ...vehicleDamages,
        // isVehicleDamaged: false,
      },
    };
  },

  // Deactivates specific vehicle
  [putVehicleRetirement]: (state, action) => {
    const incomingIds = action.payload;
    const vehicleId = incomingIds[0];
    const userId = incomingIds[1];
    const userName = incomingIds[2];

    return {
      ...state,
      [vehicleId]: {
        ...state[vehicleId],
        active: false,
        retiredOn: getDateComparisonNumber(new Date()),
        retiredBy: userId,
        retiredByUsername:userName,  //display name
      },
    };
  },

  // Reactivates specific vehicle
  [putVehicleReinstated]: (state, action) => {
    const vehicleId = action.payload;

    return {
      ...state,
      [vehicleId]: {
        ...state[vehicleId],
        active: true,
        retiredOn: null,
        retiredBy: null,
      },
    };
  },

  // validates vehicle usage
  [putVehicleUsage]: (state, action) => {
    const vehicleId = action.payload[0];
    const inUse = action.payload[1];
    return {
      ...state,
      [vehicleId]: {
        ...state[vehicleId],
        inAnyUse: inUse,
      },
    };
  },

  // validates vehicle saved inspection usage
  [putVehicleSavedUsage]: (state, action) => {
    const vehicleId = action.payload[0];
    const inUse = action.payload[1];
    return {
      ...state,
      [vehicleId]: {
        ...state[vehicleId],
        inSavedUse: inUse,
      },
    };
  },
});

export default vehicleReducer;
